import React, { useEffect, useState } from 'react';
import isInt from 'validator/lib/isInt';
import isFloat from 'validator/lib/isFloat';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  ThemeProvider,
  createTheme,
  useTheme,
} from '@mui/material';
import { useBlocker, useNavigate } from 'react-router-dom';
import {
  AGE_MAX,
  AGE_MIN,
  EXERCISE_LEVELS_CODES_AND_LABELS,
  GENDERS_CODES_AND_LABELS,
  GOALS_CODES_AND_LABELS,
  HEIGHT_UNITS_IMPERIAL,
  QUESTION_AGE,
  QUESTION_EXERCISE_LEVEL,
  QUESTION_GENDER,
  QUESTION_GOAL,
  QUESTION_HEIGHT,
  QUESTION_TARGET_WEIGHT,
  QUESTION_TARGET_WEIGHT_DURATION,
  QUESTION_WEIGHT,
  TARGET_DURATION_MAX,
  TARGET_DURATION_MIN,
  WEIGHT_UNITS_IMPERIAL,
  findAnswerFor,
  isWeightChangeTooAggressive,
  replaceAnswer,
  targetCaloriesFromOnboardingAnswers,
} from '../../services/space_onboarding';
import WeightInput from '../common/weight_input';
import WeightUnitChanger from '../common/weight_unit_changer';
import HeightInput from '../common/height_input';
import HeightUnitChanger from '../common/height_unit_changer';
import { quantityForMetric, unitForMetric } from '../../services/nutrition';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { useSyncSpaceMembershipMutation } from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { userIsCreator } from '../../services/users';
import ConfirmationModal from '../common/confirmation_modal';

const CUSTOM_CALORIES = 'CUSTOM_CALORIES';
const CALCULATED_CALORIES = 'CALCULATED_CALORIES';

const NUTRITION_HEADER_DEFAULT = 'NUTRITION_HEADER_DEFAULT';
const NUTRITION_HEADER_ALWAYS_SHOW = 'NUTRITION_HEADER_ALWAYS_SHOW';
const NUTRITION_HEADER_ALWAYS_HIDE = 'NUTRITION_HEADER_ALWAYS_HIDE';

const buildPageTheme = (appTheme) => {
  return createTheme(appTheme, {
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: 'black',
            '&:before': {
              borderBottomColor: 'black !important',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInput-underline:after': {
              borderBottomColor: 'black !important',
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: '3px 9px',
            color: 'black',
            '&.Mui-checked': {
              color: 'black',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {},
        },
      },
    },
  });
};

const isValidTargetCalories = (calorieValue) =>
  isFloat(calorieValue, {
    gt: 200,
    lt: 99999,
  });

const UserPersonalInformationMobileView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const appTheme = useTheme();

  const [isDirty, setIsDirty] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const pageTheme = buildPageTheme(appTheme);
  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const [heightUnits, setHeightUnits] = useState(HEIGHT_UNITS_IMPERIAL);
  const [weightUnits, setWeightUnits] = useState(WEIGHT_UNITS_IMPERIAL);

  const initialCustomTargetCalories =
    currentSpaceMembership?.customTargetCalories;

  const initialOnboardingAnswers = currentSpaceMembership?.onboardingAnswers;

  const [onboardingAnswers, setOnboardingAnswers] = useState(
    initialOnboardingAnswers,
  );

  useEffect(() => {
    setOnboardingAnswers(initialOnboardingAnswers);
  }, [initialOnboardingAnswers]);

  const [customTargetCalories, setCustomTargetCalories] = useState(
    initialCustomTargetCalories ? initialCustomTargetCalories.toString() : '',
  );

  const initialShowNutritionHeader =
    currentSpaceMembership?.showNutritionHeader;

  const [nutritionHeaderChoice, setNutritionHeaderChoice] = useState(
    // eslint-disable-next-line no-nested-ternary
    initialShowNutritionHeader === null ||
      initialShowNutritionHeader === undefined
      ? NUTRITION_HEADER_DEFAULT
      : initialShowNutritionHeader
      ? NUTRITION_HEADER_ALWAYS_SHOW
      : NUTRITION_HEADER_ALWAYS_HIDE,
  );

  const targetCaloriesDerivedFromAnswers =
    onboardingAnswers && targetCaloriesFromOnboardingAnswers(onboardingAnswers);

  const [targetCaloriesChoice, setTargetCaloriesChoice] = useState(
    initialCustomTargetCalories ? CUSTOM_CALORIES : CALCULATED_CALORIES,
  );

  useEffect(() => {
    setCustomTargetCalories(
      initialCustomTargetCalories ? initialCustomTargetCalories.toString() : '',
    );
    setTargetCaloriesChoice(
      initialCustomTargetCalories ? CUSTOM_CALORIES : CALCULATED_CALORIES,
    );
  }, [initialCustomTargetCalories]);

  useEffect(() => {
    setNutritionHeaderChoice(
      // eslint-disable-next-line no-nested-ternary
      initialShowNutritionHeader === null ||
        initialShowNutritionHeader === undefined
        ? NUTRITION_HEADER_DEFAULT
        : initialShowNutritionHeader
        ? NUTRITION_HEADER_ALWAYS_SHOW
        : NUTRITION_HEADER_ALWAYS_HIDE,
    );
  }, [initialShowNutritionHeader]);

  const answerFor = (questionID) =>
    findAnswerFor(questionID, onboardingAnswers);

  const [syncSpaceMembership] = useSyncSpaceMembershipMutation();

  const saveValues = async (answers, calTargets) => {
    await syncSpaceMembership({
      spaceMembershipID: currentSpaceMembership.id,
      onboardingAnswers: answers,
      onboardingAnswersLastUpdatedAt: new Date().toISOString(),
      customTargetCalories: calTargets,
      welcomeVideoWatched: currentSpaceMembership.welcomeVideoWatched,
      initialProgrammeSelectionLastOfferedAt:
        currentSpaceMembership.initialProgrammeSelectionLastOfferedAt,
      chatNickname: currentSpaceMembership.chatNickname,
      showNutritionHeader:
        // eslint-disable-next-line no-nested-ternary
        nutritionHeaderChoice === NUTRITION_HEADER_ALWAYS_SHOW
          ? true
          : nutritionHeaderChoice === NUTRITION_HEADER_ALWAYS_HIDE
          ? false
          : null,
    });

    dispatch(
      showGlobalSnackbar({
        notificationText: 'Changes saved',
      }),
    );
  };

  const answersValidationErrorFor = (newAnswers) => {
    const weight = findAnswerFor(QUESTION_WEIGHT, newAnswers);
    const targetWeight = findAnswerFor(QUESTION_TARGET_WEIGHT, newAnswers);
    const durationWeeks = findAnswerFor(
      QUESTION_TARGET_WEIGHT_DURATION,
      newAnswers,
    );
    console.log({ weight, targetWeight, durationWeeks });
    if (isWeightChangeTooAggressive(weight, targetWeight, durationWeeks)) {
      return 'Weight loss more than 1% of body weight per week is not recommended. Please change your target weight or duration';
    }
    return null;
  };

  const isValid =
    !answersValidationErrorFor(onboardingAnswers) &&
    (!targetCaloriesChoice ||
      targetCaloriesChoice !== CUSTOM_CALORIES ||
      isValidTargetCalories(customTargetCalories));

  const validationError = answersValidationErrorFor(onboardingAnswers);

  const setAnswer = (questionID, answerRaw) => {
    const newAnswers = replaceAnswer(questionID, answerRaw, onboardingAnswers);
    setOnboardingAnswers(newAnswers);
    setIsDirty(true);
  };

  console.log(JSON.stringify(onboardingAnswers));

  const age = answerFor(QUESTION_AGE);

  const [ageStr, setAgeStr] = useState(age ? age.toString() : '');

  useEffect(() => {
    setAgeStr(age ? age.toString() : '');
  }, [age]);

  const targetWeightDuration = answerFor(QUESTION_TARGET_WEIGHT_DURATION);

  const [targetWeightDurationStr, setTargetWeightDurationStr] = useState(
    targetWeightDuration ? targetWeightDuration.toString() : '',
  );

  const isValidAgeInput = (ageInput) =>
    isInt(ageInput, { gt: AGE_MIN - 1, lt: AGE_MAX + 1 });

  const isValidTargetWeightDurationInput = (durationInput) =>
    isInt(durationInput, {
      gt: TARGET_DURATION_MIN - 1,
      lt: TARGET_DURATION_MAX + 1,
    });

  const onChangeAge = (ev) => {
    const ageInput = ev.target.value;
    setAgeStr(ageInput);
    if (isValidAgeInput(ageInput)) {
      setAnswer(QUESTION_AGE, parseInt(ageInput, 10));
    }
  };

  const onChangeTargetWeightDuration = (ev) => {
    const targetWeightDurationInput = ev.target.value;
    setTargetWeightDurationStr(targetWeightDurationInput);
    if (isValidTargetWeightDurationInput(targetWeightDurationInput)) {
      setAnswer(
        QUESTION_TARGET_WEIGHT_DURATION,
        parseInt(targetWeightDurationInput, 10),
      );
    }
  };

  const onChangeNutritionHeaderChoice = (ev) => {
    setNutritionHeaderChoice(ev.target.value);
    setIsDirty(true);
  };

  const onChangeCustomTargetCalories = (newValue) => {
    setCustomTargetCalories(newValue);
    setIsDirty(true);
  };

  const onChangeTargetCaloriesChoice = (newValue) => {
    setTargetCaloriesChoice(newValue);
    if (newValue === CALCULATED_CALORIES) {
      setCustomTargetCalories('');
    }
    setIsDirty(true);
  };

  useBlocker(({ nextLocation }) => {
    console.log('useBlocker called: ', { isDirty, nextLocation });
    if (isDirty) {
      setShowConfirmModal(true);
      setPendingNavigation(nextLocation);
      return true; // Block navigation initially
    }
    return false; // Allow navigation
  });

  const handleConfirmNavigation = () => {
    console.log('handleConfirmNavigation ', { pendingNavigation });
    setShowConfirmModal(false);
    setIsDirty(false);
    // Allow navigation to proceed
    if (pendingNavigation) {
      // if (pendingNavigation.pathname === -1) {
      navigate(-1);
      // } else {
      //   navigate(pendingNavigation.pathname + pendingNavigation.search);
      // }
    }
    setPendingNavigation(null);
  };

  const handleCancelNavigation = () => {
    console.log('handleCancelNavigation');
    setShowConfirmModal(false);
    setPendingNavigation(null);
    // Stay on current page
  };

  const handleBackClick = () => {
    if (isDirty) {
      setShowConfirmModal(true);
      setPendingNavigation({ pathname: -1 }); // Special case for navigate(-1)
    } else {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    if (isValid) {
      setIsLoading(true);
      try {
        await saveValues(onboardingAnswers, parseFloat(customTargetCalories));
        setIsDirty(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!onboardingAnswers) {
    return null;
  }

  return (
    <ThemeProvider theme={pageTheme}>
      <div className="userProfile light-background">
        <div className="personalInformationForm">
          <Grid
            container
            rowSpacing={3}
            style={{
              textAlign: 'left',
            }}>
            <Grid
              item
              xs={12}
              style={{
                display: 'flex',
                margin: '23px 23px 10px',
              }}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                href="#"
                style={{ textDecoration: 'none' }}
                onClick={(ev) => {
                  ev.preventDefault();
                  handleBackClick();
                }}>
                <FontAwesomeIcon icon="chevron-left" />
                Back
              </a>
            </Grid>
            <Grid item xs={12}>
              <h2
                style={{
                  fontSize: '24px',
                  margin: '0 23px 10px',
                }}>
                Personal information
              </h2>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your body weight goals
              </h3>
            </Grid>
            <Grid
              item
              xs={12}
              style={{
                margin: '0 23px',
              }}>
              <RadioGroup
                value={answerFor(QUESTION_GOAL)}
                onChange={(ev) => {
                  setAnswer(QUESTION_GOAL, ev.target.value);
                }}>
                {Object.keys(GOALS_CODES_AND_LABELS).map((goalCode) => (
                  <FormControlLabel
                    key={goalCode}
                    control={<Radio />}
                    value={goalCode}
                    label={
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {GOALS_CODES_AND_LABELS[goalCode]}
                      </span>
                    }
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your gender
              </h3>
            </Grid>
            <Grid
              item
              xs={12}
              style={{
                margin: '0 23px',
              }}>
              <RadioGroup
                value={answerFor(QUESTION_GENDER)}
                onChange={(ev) => {
                  setAnswer(QUESTION_GENDER, ev.target.value);
                }}>
                {Object.keys(GENDERS_CODES_AND_LABELS).map((genderCode) => (
                  <FormControlLabel
                    key={genderCode}
                    control={<Radio />}
                    value={genderCode}
                    label={
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        {GENDERS_CODES_AND_LABELS[genderCode]}
                      </span>
                    }
                    style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your age
              </h3>
            </Grid>
            <Grid item xs={12}>
              <TextField
                variant="standard"
                type="number"
                InputProps={{ min: AGE_MIN, max: AGE_MAX }}
                value={ageStr}
                onChange={onChangeAge}
                sx={{
                  width: 60,
                  margin: '0 23px',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your weight
              </h3>
            </Grid>
            <Grid item xs={12}>
              <Grid container style={{ margin: '0 23px' }}>
                <WeightInput
                  valueKg={answerFor(QUESTION_WEIGHT)}
                  weightUnits={weightUnits}
                  onChange={(newWeightKg) =>
                    setAnswer(QUESTION_WEIGHT, newWeightKg)
                  }
                  onSetIsValidInput={() => {}}
                />
                <Grid
                  item
                  xs={12}
                  style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                  }}>
                  <WeightUnitChanger
                    weightUnits={weightUnits}
                    setWeightUnits={setWeightUnits}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your height
              </h3>
            </Grid>
            <Grid item xs={12}>
              <Grid container style={{ margin: '0 23px', width: '280px' }}>
                <HeightInput
                  heightCm={answerFor(QUESTION_HEIGHT)}
                  heightUnits={heightUnits}
                  onChange={(newHeightCm) =>
                    setAnswer(QUESTION_HEIGHT, newHeightCm)
                  }
                  onSetIsValidInput={() => {}}
                />
                <Grid
                  item
                  xs={12}
                  style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                  }}>
                  <HeightUnitChanger
                    heightUnits={heightUnits}
                    setHeightUnits={setHeightUnits}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your activity level
              </h3>
            </Grid>
            <Grid
              item
              xs={12}
              style={{
                margin: '0 23px',
              }}>
              <RadioGroup
                value={answerFor(QUESTION_EXERCISE_LEVEL)}
                onChange={(ev) => {
                  setAnswer(QUESTION_EXERCISE_LEVEL, ev.target.value);
                }}>
                {Object.keys(EXERCISE_LEVELS_CODES_AND_LABELS).map(
                  (goalCode) => (
                    <FormControlLabel
                      key={goalCode}
                      control={<Radio />}
                      value={goalCode}
                      label={
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          {EXERCISE_LEVELS_CODES_AND_LABELS[goalCode]}
                        </span>
                      }
                      style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                      }}
                    />
                  ),
                )}
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Your target weight
              </h3>
            </Grid>
            <Grid item xs={12}>
              <Grid container style={{ margin: '0 23px' }}>
                <WeightInput
                  valueKg={answerFor(QUESTION_TARGET_WEIGHT)}
                  weightUnits={weightUnits}
                  onChange={(newWeightKg) =>
                    setAnswer(QUESTION_TARGET_WEIGHT, newWeightKg)
                  }
                  onSetIsValidInput={() => {}}
                />
                <Grid
                  item
                  xs={12}
                  style={{
                    marginTop: '20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                  }}>
                  <WeightUnitChanger
                    weightUnits={weightUnits}
                    setWeightUnits={setWeightUnits}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Target duration
              </h3>
            </Grid>
            {validationError && (
              <Grid item xs={12}>
                <div
                  style={{
                    paddingTop: '5px',
                    marginLeft: '23px',
                    color: 'red',
                  }}>
                  {validationError}
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={3}>
                  <TextField
                    variant="standard"
                    type="number"
                    InputProps={{
                      min: TARGET_DURATION_MIN,
                      max: TARGET_DURATION_MAX,
                    }}
                    value={targetWeightDurationStr}
                    onChange={onChangeTargetWeightDuration}
                    sx={{
                      width: 60,
                      margin: '0 23px',
                    }}
                  />
                </Grid>
                <Grid item xs={9} style={{ display: 'flex' }}>
                  <div style={{ margin: 'auto 0' }}>weeks</div>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Energy Targets
              </h3>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                style={{
                  margin: '0 23px',
                }}
                value={targetCaloriesChoice}
                onChange={(ev) => {
                  onChangeTargetCaloriesChoice(ev.target.value);
                }}>
                <FormControlLabel
                  control={<Radio />}
                  value={CALCULATED_CALORIES}
                  label={
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Use calculated calories:{' '}
                      {quantityForMetric(
                        'calories',
                        targetCaloriesDerivedFromAnswers,
                      )}{' '}
                      {unitForMetric('calories')}
                    </span>
                  }
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
                <FormControlLabel
                  control={<Radio />}
                  value={CUSTOM_CALORIES}
                  label={
                    <div
                      style={{
                        display: 'flex',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}>
                      <span style={{ margin: 'auto 0' }}>
                        Custom calorie target
                      </span>
                      <TextField
                        variant="standard"
                        type="number"
                        disabled={targetCaloriesChoice !== CUSTOM_CALORIES}
                        InputProps={{ min: 200, max: 99999 }}
                        value={customTargetCalories || ''}
                        onChange={(ev) =>
                          onChangeCustomTargetCalories(ev.target.value)
                        }
                        sx={{
                          width: 60,
                          margin: '0 23px',
                        }}
                      />{' '}
                      <span style={{ margin: 'auto 0' }}>kCal</span>
                    </div>
                  }
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <h3
                style={{
                  margin: '0 23px',
                }}>
                Nutrition header
              </h3>
            </Grid>
            <Grid item xs={12}>
              <RadioGroup
                style={{
                  margin: '0 23px',
                }}
                value={nutritionHeaderChoice}
                onChange={onChangeNutritionHeaderChoice}>
                <FormControlLabel
                  control={<Radio />}
                  value={NUTRITION_HEADER_DEFAULT}
                  label={
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Show nutrition header if the program needs it
                    </span>
                  }
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
                <FormControlLabel
                  control={<Radio />}
                  value={NUTRITION_HEADER_ALWAYS_SHOW}
                  label={
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Always show nutrition header
                    </span>
                  }
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
                <FormControlLabel
                  control={<Radio />}
                  value={NUTRITION_HEADER_ALWAYS_HIDE}
                  label={
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Always hide nutrition header
                    </span>
                  }
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
              </RadioGroup>
            </Grid>
          </Grid>
        </div>
        <div style={{ height: '79px' }}>
          <Grid container>
            <Grid
              item
              xs={12}
              style={{ margin: '15px 0', padding: '6px', textAlign: 'left' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                style={{ marginLeft: '17px' }}
                disabled={!isDirty || !isValid || isLoading}>
                Save
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleBackClick}
                style={{ marginLeft: '6px' }}>
                Back
              </Button>
              {isLoading && (
                <CircularProgress size={20} style={{ margin: 'auto 10px' }} />
              )}
            </Grid>
          </Grid>
        </div>
      </div>

      <ConfirmationModal
        open={showConfirmModal}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmText="Leave Page"
        cancelText="Stay Here"
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        confirmColor="error"
        cancelColor="primary"
      />
    </ThemeProvider>
  );
};

export default UserPersonalInformationMobileView;
